/**
 * Shared utilities for chart preview rendering.
 */

const COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6'];

function multiSeriesTooltipFormatter(params) {
  if (!Array.isArray(params)) return '';
  const visible = params.filter(p => p.value != null);
  if (visible.length === 0) return '';
  let html = `${visible[0].axisValueLabel || visible[0].name || ''}<br/>`;
  visible.forEach(p => {
    const val = typeof p.value === 'number' ? p.value.toLocaleString() : p.value;
    html += `${p.marker} ${p.seriesName}: <strong>${val}</strong><br/>`;
  });
  return html;
}

/**
 * Returns true when two or more numeric series differ in magnitude by 10x or more,
 * which would make the smaller series visually flat on a shared Y-axis.
 */
function hasMagnitudeMismatch(data, fields) {
  if (fields.length < 2 || data.length === 0) return false;
  const maxVals = fields.map((f) =>
    Math.max(...data.map((d) => Math.abs(Number(d[f]) || 0)))
  );
  const maxVal = Math.max(...maxVals);
  const minNonZero = Math.min(...maxVals.filter((v) => v > 0));
  return minNonZero > 0 && maxVal / minNonZero >= 10;
}

/**
 * Build a dual Y-axis bar+line combo option.
 * First series → bar on left axis; remaining series → smooth line on right axis.
 */
function buildDualAxisOption(data, xField, valueFields, seriesNames, chartType) {
  const leftColor = COLORS[0];
  const rightColor = COLORS[1];

  return {
    animation: true,
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
    legend: { data: seriesNames, bottom: 0, textStyle: { color: '#666' } },
    grid: { left: '12%', right: '12%', bottom: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: data.map((d) => String(d[xField])),
      axisLabel: { color: '#666' },
    },
    yAxis: [
      {
        type: 'value',
        name: seriesNames[0],
        nameLocation: 'middle',
        nameGap: 50,
        nameRotate: 90,
        nameTextStyle: { color: leftColor, fontWeight: 'bold', fontSize: 11 },
        axisLine: { show: true, lineStyle: { color: leftColor } },
        axisLabel: { color: leftColor },
      },
      {
        type: 'value',
        name: seriesNames[1] || seriesNames[0],
        nameLocation: 'middle',
        nameGap: 55,
        nameRotate: 90,
        position: 'right',
        nameTextStyle: { color: rightColor, fontWeight: 'bold', fontSize: 11 },
        axisLine: { show: true, lineStyle: { color: rightColor } },
        axisLabel: { color: rightColor },
      },
    ],
    series: valueFields.map((field, i) => ({
      name: seriesNames[i],
      type: i === 0 ? (chartType === 'area' ? 'line' : 'bar') : 'line',
      yAxisIndex: i === 0 ? 0 : 1,
      data: data.map((d) => d[field]),
      smooth: i > 0,
      areaStyle: chartType === 'area' && i === 0 ? { opacity: 0.3 } : undefined,
      itemStyle: {
        color: COLORS[i % COLORS.length],
        borderRadius: i === 0 && chartType === 'bar' ? [4, 4, 0, 0] : undefined,
      },
      z: i > 0 ? 3 : 2,
    })),
  };
}

export function formatCellValue(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'object') {
    if (value.value !== undefined) {
      return String(value.value);
    }
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Build ECharts option for chart preview based on chart type and data.
 * @param {string} chartType
 * @param {Array} chartData
 * @param {Object} config
 */
export function getPreviewOption(chartType, chartData, config) {
  if (!chartData || chartData.length === 0) return null;

  const cfg = config?.config || config || {};
  const baseOption = {
    animation: true,
    tooltip: {
      trigger: ['pie', 'donut', 'scatter'].includes(chartType) ? 'item' : 'axis',
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    backgroundColor: 'transparent',
  };

  switch (chartType) {
    case 'bar':
    case 'line':
    case 'area':
    case 'scatter': {
      const keys = Object.keys(chartData[0]);
      const xField = cfg.xAxis?.field || keys[0];
      const seriesType = chartType === 'area' ? 'line' : chartType;

      // Determine value fields: use all configured series, or auto-detect from columns
      const configuredFields = (cfg.series || []).map((s) => s.field).filter(Boolean);
      const valueFields =
        configuredFields.length > 0
          ? configuredFields
          : keys.filter((k) => k !== xField);

      const primaryValueField = valueFields[0] || keys[keys.length - 1];

      // Detect grouped data: 3+ columns and duplicate x-axis values
      const xValues = chartData.map((d) => d[xField]);
      const hasDuplicateX = new Set(xValues).size < xValues.length;
      const groupField = keys.find((k) => k !== xField && !valueFields.includes(k));

      if (hasDuplicateX && groupField) {
        const groups = [...new Set(chartData.map((d) => d[groupField]))];
        const categories = [...new Set(chartData.map((d) => String(d[xField])))];

        const pivoted = {};
        for (const row of chartData) {
          const g = row[groupField];
          if (!pivoted[g]) pivoted[g] = {};
          pivoted[g][String(row[xField])] = row[primaryValueField];
        }

        return {
          ...baseOption,
          tooltip: { trigger: 'axis', formatter: multiSeriesTooltipFormatter },
          legend: { data: groups.map(String), bottom: 0, textStyle: { color: '#666' } },
          grid: { left: '3%', right: '4%', bottom: '12%', containLabel: true },
          xAxis: {
            type: 'category',
            data: categories,
            axisLabel: { color: '#666', rotate: categories.length > 6 ? 30 : 0 },
          },
          yAxis: { type: 'value', axisLabel: { color: '#666' } },
          series: groups.map((g, i) => ({
            name: String(g),
            type: seriesType,
            data: categories.map((cat) => pivoted[g]?.[cat] ?? null),
            areaStyle: chartType === 'area' ? { opacity: 0.3 } : undefined,
            symbolSize: chartType === 'scatter' ? 12 : undefined,
            itemStyle: {
              color: COLORS[i % COLORS.length],
              borderRadius: chartType === 'bar' ? [4, 4, 0, 0] : undefined,
            },
            smooth: chartType === 'line' || chartType === 'area',
          })),
        };
      }

      // Multi-series layout: respect explicit user preference or auto-detect
      const layout = cfg.multiSeriesLayout || 'auto';
      if (valueFields.length >= 2) {
        const shouldDualAxis = layout === 'dual_axis' ||
          (layout === 'auto' && hasMagnitudeMismatch(chartData, valueFields));
        if (shouldDualAxis && layout !== 'shared_axis') {
          const seriesNames = valueFields.map((f, i) => cfg.series?.[i]?.name || f);
          return buildDualAxisOption(chartData, xField, valueFields, seriesNames, chartType);
        }
      }

      // Multi-measure: create a series for each value field on a shared axis
      const seriesList = valueFields.map((field, i) => {
        const cfgEntry = cfg.series?.[i];
        return {
          name: cfgEntry?.name || field,
          type: seriesType,
          data: chartData.map((d) => d[field]),
          areaStyle: chartType === 'area' ? { opacity: 0.3 } : undefined,
          symbolSize: chartType === 'scatter' ? 12 : undefined,
          itemStyle: {
            color: COLORS[i % COLORS.length],
            borderRadius: chartType === 'bar' ? [4, 4, 0, 0] : undefined,
          },
          smooth: chartType === 'line' || chartType === 'area',
        };
      });

      return {
        ...baseOption,
        legend: valueFields.length > 1
          ? { data: seriesList.map((s) => s.name), bottom: 0, textStyle: { color: '#666' } }
          : undefined,
        grid: {
          left: '3%',
          right: '4%',
          bottom: valueFields.length > 1 ? '12%' : '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: chartData.map((d) => d[xField]),
          axisLabel: { color: '#666' },
        },
        yAxis: { type: 'value', axisLabel: { color: '#666' } },
        series: seriesList,
      };
    }

    case 'pie':
    case 'donut':
      return {
        ...baseOption,
        series: [
          {
            type: 'pie',
            radius: chartType === 'donut' ? ['40%', '70%'] : '70%',
            data: chartData.map((d) => ({
              name: d[cfg.xAxis?.field || Object.keys(d)[0]],
              value: d[cfg.series?.[0]?.field || Object.keys(d)[1]],
            })),
            label: { color: '#666' },
          },
        ],
      };

    case 'heatmap': {
      const keys = Object.keys(chartData[0] || {});
      const xField = cfg.xAxis?.field || keys[0];
      const yField = cfg.yAxis?.field || keys[1];
      const valueField = cfg.series?.[0]?.field || keys[2] || keys[1];

      const xValues = [...new Set(chartData.map((d) => String(d[xField])))];
      const yValues = [...new Set(chartData.map((d) => String(d[yField])))];

      const heatmapData = chartData.map((d) => {
        const xIndex = xValues.indexOf(String(d[xField]));
        const yIndex = yValues.indexOf(String(d[yField]));
        return [xIndex, yIndex, d[valueField] || 0];
      });

      const values = heatmapData.map((d) => d[2]);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);

      return {
        ...baseOption,
        xAxis: {
          type: 'category',
          data: xValues,
          splitArea: { show: true },
        },
        yAxis: {
          type: 'category',
          data: yValues,
          splitArea: { show: true },
        },
        visualMap: {
          min: minValue,
          max: maxValue,
          calculable: true,
          orient: 'vertical',
          right: '2%',
          top: 'center',
          inRange: {
            color: [
              '#313695', '#4575b4', '#74add1', '#abd9e9', '#ffffbf',
              '#fee090', '#fdae61', '#f46d43', '#d73027',
            ],
          },
        },
        series: [
          {
            type: 'heatmap',
            data: heatmapData,
            label: { show: heatmapData.length <= 100 },
          },
        ],
      };
    }

    case 'geo':
      return null;

    case 'funnel': {
      const xField = cfg.xAxis?.field || Object.keys(chartData[0])[0];
      const yField = cfg.series?.[0]?.field || Object.keys(chartData[0])[1];
      const sortedData = [...chartData].sort((a, b) => (b[yField] || 0) - (a[yField] || 0));
      const funnelColors = [
        '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
        '#f43f5e', '#fb7185', '#fda4af', '#fecdd3', '#ffe4e6',
      ];

      return {
        ...baseOption,
        tooltip: { trigger: 'item', formatter: '{b}: {c}' },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          data: sortedData.map((d) => String(d[xField])),
        },
        series: [
          {
            name: cfg.series?.[0]?.name || 'Funnel',
            type: 'funnel',
            left: '10%',
            top: 40,
            bottom: 40,
            width: '60%',
            min: 0,
            max: Math.max(...sortedData.map((d) => d[yField] || 0)),
            minSize: '0%',
            maxSize: '100%',
            sort: 'descending',
            gap: 2,
            label: { show: true, position: 'inside', formatter: '{b}' },
            labelLine: { show: false },
            itemStyle: { borderColor: '#fff', borderWidth: 1 },
            emphasis: { label: { fontSize: 14 } },
            data: sortedData.map((d, index) => ({
              name: String(d[xField]),
              value: d[yField] || 0,
              itemStyle: { color: funnelColors[index % funnelColors.length] },
            })),
          },
        ],
      };
    }

    default:
      return null;
  }
}
